import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Camera, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ProgressOverlay, { fmtWeight } from "@/components/progress/ProgressOverlay";

// Coach-side progress photo comparison. Read-only: the coach picks any two of
// the athlete's photos and drags the overlay to show the change.
export default function CoachPhotosTab({ email, name, weightUnit = "kg" }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [poseFilter, setPoseFilter] = useState("all");
  const [slot, setSlot] = useState("after");
  const [beforeId, setBeforeId] = useState(null);
  const [afterId, setAfterId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/coach/athletes/${encodeURIComponent(email)}/progress-photos`)
      .then(({ data }) => {
        const sorted = [...(data.photos || [])].sort((a, b) => a.date.localeCompare(b.date));
        setPhotos(sorted);
        if (sorted.length) {
          setBeforeId(sorted[0].id);
          setAfterId(sorted[sorted.length - 1].id);
        } else { setBeforeId(null); setAfterId(null); }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load photos"))
      .finally(() => setLoading(false));
  }, [email]);

  const urlFor = (path) => {
    const tok = localStorage.getItem("spc_token") || sessionStorage.getItem("spc_token") || "";
    return `${api.defaults.baseURL}/coach/athletes/${encodeURIComponent(email)}/progress-photo/${path}?auth=${encodeURIComponent(tok)}`;
  };

  const filtered = useMemo(
    () => poseFilter === "all" ? photos : photos.filter((p) => (p.pose || "front") === poseFilter),
    [photos, poseFilter]);
  const find = (id) => photos.find((p) => p.id === id) || null;
  const before = find(beforeId), after = find(afterId);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#E2E30A]" /></div>;
  }
  if (photos.length < 2) {
    return (
      <div className="border border-dashed border-white/15 bg-[#0F0F0F] p-8 text-center" data-testid="coach-photos-empty">
        <Camera className="h-8 w-8 text-zinc-500 mx-auto mb-3" />
        <p className="text-sm text-zinc-300">
          {photos.length === 0 ? "No progress photos shared yet." : "Only one photo so far — two are needed to compare."}
        </p>
        <p className="text-[11px] text-zinc-500 mt-1">Athletes add these from Tracker → Daily Snapshot → Progress.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="coach-photos-tab">
      <ProgressOverlay before={before} after={after} urlFor={urlFor}
        weightUnit={weightUnit} athleteName={name || email.split("@")[0]} />

      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400">
          Picking for <span className="font-bold" style={{ color: slot === "before" ? "#4C9AFF" : "#FB7185" }}>
            {slot === "before" ? "Before" : "After"}</span>
        </p>
        <button type="button" data-testid="coach-photos-slot-toggle"
          onClick={() => setSlot((s) => (s === "before" ? "after" : "before"))}
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-300 hover:text-white transition-colors">
          <ArrowLeftRight className="h-3.5 w-3.5" /> Switch slot
        </button>
        <div className="ml-auto inline-flex flex-wrap gap-1 border border-white/10 p-1" data-testid="coach-photos-pose-filter">
          {["all", "front", "side", "back", "custom"].map((k) => (
            <button key={k} type="button" data-testid={`coach-photos-pose-${k}`}
              onClick={() => setPoseFilter(k)}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold transition-colors ${poseFilter === k ? "bg-[#FB7185] text-black" : "text-zinc-300 hover:text-white"}`}>
              {k}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5 max-h-64 overflow-y-auto pr-1" data-testid="coach-photos-strip">
        {filtered.map((p) => {
          const isB = p.id === beforeId, isA = p.id === afterId;
          const colour = isB ? "#4C9AFF" : isA ? "#FB7185" : null;
          return (
            <button key={p.id} type="button" data-testid={`coach-photos-pick-${p.id}`}
              onClick={() => {
                if (slot === "before") { setBeforeId(p.id); setSlot("after"); }
                else { setAfterId(p.id); setSlot("before"); }
              }}
              className="relative aspect-square overflow-hidden border-2 hover:opacity-90 transition-opacity"
              style={{ borderColor: colour || "rgba(255,255,255,0.08)" }}
              title={`${p.date} · ${p.pose || "front"}${p.weightKg != null ? " · " + fmtWeight(p.weightKg, weightUnit) : ""}`}>
              <img src={urlFor(p.photoPath)} alt="progress" className="w-full h-full object-cover" />
              {colour && (
                <span className="absolute top-1 left-1 text-[8px] font-bold uppercase tracking-widest px-1 py-0.5 text-black"
                  style={{ background: colour }}>{isB ? "B" : "A"}</span>
              )}
              <span className="absolute inset-x-0 bottom-0 bg-black/70 text-[8px] text-white font-mono px-1 py-0.5 truncate">
                {p.date.slice(5)}
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-zinc-500">
        Private to you and the athlete. Nothing is shared unless one of you saves the image.
      </p>
    </div>
  );
}
